import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Chip
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
  NavigateBefore as BackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Home as AddressIcon,
  Today as DateIcon,
  FileDownload as ExcelIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { customerService } from '../../services/customerService';
import { enqueueSnackbar } from 'notistack';
import axios from 'axios';
import { getAuthHeader } from '../../services/authService';
import API_CONFIG from '../../config';
import creditService from '../../services/creditService';

// Add print-specific styles to hide sidebar and navbar
const printStyles = `
  @media print {
    /* Hide all navigation elements */
    nav, header, footer, aside, .MuiAppBar-root, .MuiDrawer-root, .MuiDrawer-paper, .MuiToolbar-root, 
    .MuiIconButton-root, .MuiBreadcrumbs-root, button, .no-print, .MuiTablePagination-root {
      display: none !important;
    }
    
    /* Make sure Layout component doesn't affect printing */
    .MuiBox-root, main, .MuiDrawer-root ~ .MuiBox-root {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      left: 0 !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    .print-container {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      padding: 20px !important;
      margin: 0 !important;
      background-color: white !important;
    }
    
    .customer-ledger-content {
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .print-header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #ddd;
    }
    
    /* Make sure paper components display properly */
    .MuiPaper-root {
      box-shadow: none !important;
      border: 1px solid #ddd !important;
    }
    
    /* Make table more readable in print */
    .MuiTableCell-root {
      padding: 8px !important;
      font-size: 11px !important;
    }
    
    .MuiTableHead-root .MuiTableCell-root {
      font-weight: bold !important;
      background-color: #f5f5f5 !important;
    }
    
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    @page {
      size: A4;
      margin: 10mm;
    }
  }
`;

const CustomerLedger = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  
  // For ledger data
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // For account summary
  const [accountSummary, setAccountSummary] = useState({
    openingBalance: 0,
    totalInvoice: 0,
    totalPaid: 0,
    totalReturns: 0,
    balanceDue: 0
  });
  
  // Date range
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 3, 1).toISOString().split('T')[0], // April 1st of current year
    endDate: new Date(new Date().getFullYear() + 1, 2, 31).toISOString().split('T')[0] // March 31st of next year
  });
  
  // Utility function to format transaction dates
  const formatTransactionDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    console.log('Formatting transaction date:', dateValue, 'Type:', typeof dateValue);
    
    try {
      // Handle string values
      if (typeof dateValue === 'string') {
        // Check for ISO format with time (YYYY-MM-DDTHH:MM:SS)
        if (dateValue.includes('T')) {
          const dateObj = new Date(dateValue);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          }
        }
        // Check for ISO date format (YYYY-MM-DD)
        else if (dateValue.includes('-') && dateValue.length >= 10) {
          const dateObj = new Date(dateValue);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          }
        }
        // Check for numeric string that might be a timestamp or special format
        else if (/^\d+$/.test(dateValue)) {
          const numericValue = parseInt(dateValue);
          
          // Handle the specific format like "202552800" (appears to be YYYYDDDXX format)
          if (dateValue.length === 9 && dateValue.startsWith('2025')) {
            // Extract year and day of year
            const year = parseInt(dateValue.substring(0, 4));
            const dayOfYear = parseInt(dateValue.substring(4, 7)); // Get 3 digits for day of year
            
            console.log('Parsing YYYYDDDXX format:', { year, dayOfYear, original: dateValue });
            
            if (year >= 2000 && dayOfYear >= 1 && dayOfYear <= 366) {
              const dateObj = new Date(year, 0, 1); // Start with Jan 1st
              dateObj.setDate(dayOfYear); // Set to the specific day of year
              if (!isNaN(dateObj.getTime())) {
                const formatted = dateObj.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
                console.log('Successfully formatted YYYYDDDXX:', formatted);
                return formatted;
              }
            }
          }
          // Handle timestamp in milliseconds
          else if (numericValue > 1000000000000) {
            const dateObj = new Date(numericValue);
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
            }
          }
          // Handle timestamp in seconds
          else if (numericValue > 1000000000) {
            const dateObj = new Date(numericValue * 1000);
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
            }
          }
          // Handle YYYYMMDD format
          else if (dateValue.length === 8) {
            const year = dateValue.substring(0, 4);
            const month = dateValue.substring(4, 6);
            const day = dateValue.substring(6, 8);
            const dateObj = new Date(year, month - 1, day);
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
            }
          }
          // Handle other day of year formats
          else if (dateValue.length >= 6 && dateValue.length <= 8) {
            const year = dateValue.substring(0, 4);
            const dayOfYear = parseInt(dateValue.substring(4));
            if (year >= 2000 && dayOfYear >= 1 && dayOfYear <= 366) {
              const dateObj = new Date(year, 0, 1);
              dateObj.setDate(dayOfYear);
              if (!isNaN(dateObj.getTime())) {
                return dateObj.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
              }
            }
          }
        }
        // Try parsing as a general date string
        else {
          const dateObj = new Date(dateValue);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          }
        }
      }
      // Handle numeric timestamp
      else if (typeof dateValue === 'number') {
        let dateObj;
        // Check if it's in milliseconds or seconds
        if (dateValue > 1000000000000) {
          dateObj = new Date(dateValue);
        } else if (dateValue > 1000000000) {
          dateObj = new Date(dateValue * 1000);
        } else {
          dateObj = new Date(dateValue);
        }
        
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      }
      // Handle Date object
      else if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      // Handle Java LocalDateTime object format or array format
      else if (dateValue && typeof dateValue === 'object') {
        // Handle LocalDateTime object format {year, monthValue, dayOfMonth, hour, minute, second, nano}
        if (dateValue.year && dateValue.monthValue && dateValue.dayOfMonth) {
          const dateObj = new Date(dateValue.year, dateValue.monthValue - 1, dateValue.dayOfMonth);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          }
        }
        // Handle LocalDateTime array format [year, month, day, hour, minute, second, nanosecond]
        else if (Array.isArray(dateValue) && dateValue.length >= 3) {
          const year = dateValue[0];
          const month = dateValue[1]; // Note: month is 1-based in the array
          const day = dateValue[2];
          
          console.log('Parsing LocalDateTime array:', { year, month, day, original: dateValue });
          
          if (year && month && day) {
            const dateObj = new Date(year, month - 1, day); // month - 1 because Date constructor expects 0-based month
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
            }
          }
        }
      }
      
      console.warn('Could not format date:', dateValue);
      return 'Invalid Date';
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return 'Invalid Date';
    }
  };
  
  // Fetch customer details
  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      setTransactionsLoading(true);
      
      // First get customer details
      const customerResponse = await customerService.getCustomerById(customerId);
      setCustomer(customerResponse);
      
      // Fetch credit transactions and summary using the creditService
      const [creditSummary, creditTransactions] = await Promise.all([
        creditService.getCustomerCreditSummary(customerId),
        creditService.getCustomerTransactions(customerId)
      ]);
      
      // Update the summary information
      setAccountSummary({
        openingBalance: creditSummary.openingBalance || 0,
        totalInvoice: creditSummary.totalCreditSales || 0,
        totalPaid: creditSummary.totalPayments || 0,
        totalReturns: creditSummary.totalReturns || 0,
        balanceDue: creditSummary.balanceDue || 0
      });
      
      // Log the API response data
      console.log('Customer credit transactions API response:', creditTransactions);
      
      // Map credit transactions to the format expected by the ledger table
      if (creditTransactions && creditTransactions.length > 0) {
        const formattedTransactions = creditTransactions.map(tx => {
          console.log('Raw transaction object from API:', tx);
          
          // Determine if it's a debit or credit type transaction
          let transactionType = '';
          if (tx.transactionType) {
            transactionType = tx.transactionType;
          } else if (tx.transaction_type) {
            transactionType = tx.transaction_type;
          }
          
          const isDebit = ['OPENING_BALANCE', 'CREDIT_SALE'].includes(transactionType);
          
          // Extract date from transaction object
          let transactionDate = null;
          console.log('Transaction object keys:', Object.keys(tx));
          console.log('Transaction object:', JSON.stringify(tx, null, 2));
          
          if (tx.transactionDate) {
            transactionDate = tx.transactionDate;
            console.log('Found transactionDate:', transactionDate, 'Type:', typeof transactionDate);
          } else if (tx.transaction_date) {
            transactionDate = tx.transaction_date;
            console.log('Found transaction_date:', transactionDate, 'Type:', typeof transactionDate);
          } else {
            console.log('No date field found in transaction object');
          }
          
          // Format the date for display (always use transactionDate)
          let displayDate = 'N/A';
          if (transactionDate) {
            console.log('Processing transaction date:', transactionDate, 'Type:', typeof transactionDate);
            
            try {
              // Handle string values
              if (typeof transactionDate === 'string') {
                // Check for ISO format with time (YYYY-MM-DDTHH:MM:SS)
                if (transactionDate.includes('T')) {
              const dateObj = new Date(transactionDate);
              if (!isNaN(dateObj.getTime())) {
                    displayDate = dateObj.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    });
                  }
                }
                // Check for ISO date format (YYYY-MM-DD)
                else if (transactionDate.includes('-') && transactionDate.length >= 10) {
                  const dateObj = new Date(transactionDate);
                  if (!isNaN(dateObj.getTime())) {
                    displayDate = dateObj.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    });
                  }
                }
                // Check for numeric string that might be a timestamp or special format
                else if (/^\d+$/.test(transactionDate)) {
                  const numericValue = parseInt(transactionDate);
                  
                  // Handle the specific format like "202552800" (appears to be YYYYDDDXX format)
                  if (transactionDate.length === 9 && transactionDate.startsWith('2025')) {
                    // Extract year and day of year
                    const year = parseInt(transactionDate.substring(0, 4));
                    const dayOfYear = parseInt(transactionDate.substring(4, 7)); // Get 3 digits for day of year
                    
                    if (year >= 2000 && dayOfYear >= 1 && dayOfYear <= 366) {
                      const dateObj = new Date(year, 0, 1); // Start with Jan 1st
                      dateObj.setDate(dayOfYear); // Set to the specific day of year
                      if (!isNaN(dateObj.getTime())) {
                        displayDate = dateObj.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                      }
                    }
                  }
                  // Handle YYYYMMDD format
                  else if (transactionDate.length === 8) {
                    const year = transactionDate.substring(0, 4);
                    const month = transactionDate.substring(4, 6);
                    const day = transactionDate.substring(6, 8);
                    const dateObj = new Date(year, month - 1, day);
                    if (!isNaN(dateObj.getTime())) {
                      displayDate = dateObj.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                    }
                  }
                  // Handle timestamp in milliseconds
                  else if (numericValue > 1000000000000) {
                    const dateObj = new Date(numericValue);
                    if (!isNaN(dateObj.getTime())) {
                      displayDate = dateObj.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                    }
                  }
                  // Handle timestamp in seconds
                  else if (numericValue > 1000000000) {
                    const dateObj = new Date(numericValue * 1000);
                    if (!isNaN(dateObj.getTime())) {
                      displayDate = dateObj.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                    }
                  }
                  // Handle other day of year formats
                  else if (transactionDate.length >= 6 && transactionDate.length <= 8) {
                    const year = transactionDate.substring(0, 4);
                    const dayOfYear = parseInt(transactionDate.substring(4));
                    if (year >= 2000 && dayOfYear >= 1 && dayOfYear <= 366) {
                      const dateObj = new Date(year, 0, 1);
                      dateObj.setDate(dayOfYear);
                      if (!isNaN(dateObj.getTime())) {
                        displayDate = dateObj.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                      }
                    }
                  }
                }
                // Try parsing as a general date string
                else {
                  const dateObj = new Date(transactionDate);
                  if (!isNaN(dateObj.getTime())) {
                    displayDate = dateObj.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    });
                  }
                }
              }
              // Handle numeric timestamp
              else if (typeof transactionDate === 'number') {
                let dateObj;
                // Check if it's in milliseconds or seconds
                if (transactionDate > 1000000000000) {
                  dateObj = new Date(transactionDate);
                } else if (transactionDate > 1000000000) {
                  dateObj = new Date(transactionDate * 1000);
              } else {
                  dateObj = new Date(transactionDate);
                }
                
                if (!isNaN(dateObj.getTime())) {
                  displayDate = dateObj.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  });
                }
              }
              // Handle Date object
              else if (transactionDate instanceof Date) {
                displayDate = transactionDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
              }
              // Handle Java LocalDateTime object format or array format
              else if (transactionDate && typeof transactionDate === 'object') {
                // Handle LocalDateTime object format {year, monthValue, dayOfMonth, hour, minute, second, nano}
                if (transactionDate.year && transactionDate.monthValue && transactionDate.dayOfMonth) {
                  const dateObj = new Date(transactionDate.year, transactionDate.monthValue - 1, transactionDate.dayOfMonth);
                  if (!isNaN(dateObj.getTime())) {
                    displayDate = dateObj.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    });
                  }
                }
                // Handle LocalDateTime array format [year, month, day, hour, minute, second, nanosecond]
                else if (Array.isArray(transactionDate) && transactionDate.length >= 3) {
                  const year = transactionDate[0];
                  const month = transactionDate[1]; // Note: month is 1-based in the array
                  const day = transactionDate[2];
                  
                  console.log('Parsing LocalDateTime array:', { year, month, day, original: transactionDate });
                  
                  if (year && month && day) {
                    const dateObj = new Date(year, month - 1, day); // month - 1 because Date constructor expects 0-based month
                    if (!isNaN(dateObj.getTime())) {
                      displayDate = dateObj.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                      console.log('Successfully formatted LocalDateTime array:', displayDate);
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Error formatting date:', error, transactionDate);
              displayDate = 'Invalid Date';
            }
            
            console.log('Formatted date result:', displayDate);
          }
          
          // Create a formatted transaction object
          return {
            id: tx.id || 0,
            // Store original date for sorting
            rawDate: transactionDate,
            // Use pre-formatted date for display
            displayDate: displayDate,
            type: transactionType,
            reference: tx.referenceId || tx.reference_id || `TRANS-${tx.id || 0}`,
            description: getTransactionDescription(tx),
            debit: isDebit ? (parseFloat(tx.amount || 0) || parseFloat(tx.debit || 0)) : 0,
            credit: !isDebit ? (parseFloat(tx.amount || 0) || parseFloat(tx.credit || 0)) : 0,
            balance: parseFloat(tx.runningBalance || tx.running_balance || tx.balance || 0),
            status: getTransactionStatus(transactionType),
            paymentMethod: tx.paymentMethod || tx.payment_method || ''
          };
        });
        
        // Sort transactions by date in ascending order (oldest first)
        formattedTransactions.sort((a, b) => {
          // Try to compare dates safely
          try {
            console.log('Sorting transaction A:', a.rawDate, 'Transaction B:', b.rawDate);
            
            let dateA, dateB;
            
            // Parse date A
            if (a.rawDate) {
              if (typeof a.rawDate === 'string') {
                // Handle various string formats
                if (a.rawDate.includes('T')) {
                  dateA = new Date(a.rawDate);
                } else if (a.rawDate.includes('-')) {
                  dateA = new Date(a.rawDate);
                } else if (/^\d+$/.test(a.rawDate)) {
                  // Handle numeric strings
                  if (a.rawDate.length === 9 && a.rawDate.startsWith('2025')) {
                    // YYYYDDDXX format
                    const year = parseInt(a.rawDate.substring(0, 4));
                    const dayOfYear = parseInt(a.rawDate.substring(4, 7));
                    dateA = new Date(year, 0, 1);
                    dateA.setDate(dayOfYear);
                  } else if (a.rawDate.length === 8) {
                    // YYYYMMDD format
                    const year = a.rawDate.substring(0, 4);
                    const month = a.rawDate.substring(4, 6);
                    const day = a.rawDate.substring(6, 8);
                    dateA = new Date(year, month - 1, day);
                  } else {
                    dateA = new Date(parseInt(a.rawDate));
                  }
                } else {
                  dateA = new Date(a.rawDate);
                }
              } else if (typeof a.rawDate === 'number') {
                dateA = new Date(a.rawDate);
              } else if (Array.isArray(a.rawDate) && a.rawDate.length >= 3) {
                // LocalDateTime array format
                dateA = new Date(a.rawDate[0], a.rawDate[1] - 1, a.rawDate[2]);
              } else {
                dateA = new Date(a.rawDate);
              }
            } else {
              dateA = new Date(0);
            }
            
            // Parse date B
            if (b.rawDate) {
              if (typeof b.rawDate === 'string') {
                // Handle various string formats
                if (b.rawDate.includes('T')) {
                  dateB = new Date(b.rawDate);
                } else if (b.rawDate.includes('-')) {
                  dateB = new Date(b.rawDate);
                } else if (/^\d+$/.test(b.rawDate)) {
                  // Handle numeric strings
                  if (b.rawDate.length === 9 && b.rawDate.startsWith('2025')) {
                    // YYYYDDDXX format
                    const year = parseInt(b.rawDate.substring(0, 4));
                    const dayOfYear = parseInt(b.rawDate.substring(4, 7));
                    dateB = new Date(year, 0, 1);
                    dateB.setDate(dayOfYear);
                  } else if (b.rawDate.length === 8) {
                    // YYYYMMDD format
                    const year = b.rawDate.substring(0, 4);
                    const month = b.rawDate.substring(4, 6);
                    const day = b.rawDate.substring(6, 8);
                    dateB = new Date(year, month - 1, day);
                  } else {
                    dateB = new Date(parseInt(b.rawDate));
                  }
                } else {
                  dateB = new Date(b.rawDate);
                }
              } else if (typeof b.rawDate === 'number') {
                dateB = new Date(b.rawDate);
              } else if (Array.isArray(b.rawDate) && b.rawDate.length >= 3) {
                // LocalDateTime array format
                dateB = new Date(b.rawDate[0], b.rawDate[1] - 1, b.rawDate[2]);
              } else {
                dateB = new Date(b.rawDate);
              }
            } else {
              dateB = new Date(0);
            }
            
            console.log('Parsed dateA:', dateA, 'dateB:', dateB);
            
            // Check if dates are valid
            if (isNaN(dateA.getTime())) dateA = new Date(0);
            if (isNaN(dateB.getTime())) dateB = new Date(0);
            
            const result = dateA - dateB; // Ascending order (oldest first)
            console.log('Sort result:', result, 'dateA:', dateA.toISOString(), 'dateB:', dateB.toISOString());
            
            return result;
          } catch (error) {
            console.error('Error sorting dates:', error);
            return 0;
          }
        });
        
        console.log('Final sorted transactions:');
        formattedTransactions.forEach((tx, index) => {
          console.log(`${index}: ${tx.displayDate} - ${tx.type} - ${tx.description} - Raw: ${tx.rawDate}`);
        });
        
        setTransactions(formattedTransactions);
      } else {
        // Create a placeholder transaction based on customer credit balance
        const placeholderTransaction = {
          id: 1,
          displayDate: new Date().toLocaleDateString('en-GB'),
          type: 'CREDIT',
          reference: `CREDIT-${customerId}`,
          description: 'Current Credit Balance',
          debit: parseFloat(customerResponse.creditBalance || 0),
          credit: 0,
          balance: parseFloat(customerResponse.creditBalance || 0),
          status: 'PENDING'
        };
        
        setTransactions([placeholderTransaction]);
      }
      
      setLoading(false);
      setTransactionsLoading(false);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      enqueueSnackbar('Failed to load customer details', { variant: 'error' });
      setLoading(false);
      setTransactionsLoading(false);
    }
  };
  
  // Helper function to generate a meaningful description for transactions
  const getTransactionDescription = (transaction) => {
    const type = transaction.transactionType || transaction.transaction_type;
    const method = transaction.paymentMethod || transaction.payment_method || '';
    const refId = transaction.referenceId || transaction.reference_id || '';
    const notes = transaction.notes || '';
    
    switch (type) {
      case 'OPENING_BALANCE':
        return 'Opening Balance';
      case 'CREDIT_SALE':
        return `Credit Sale - ${refId ? `Order #${refId}` : 'Unknown'}`;
      case 'PAYMENT':
        return `Payment Received - ${method}${notes ? ` - ${notes}` : ''}`;
      case 'RETURN':
        return `Return - ${refId ? `Order #${refId}` : 'Unknown'}`;
      default:
        return notes || 'Transaction';
    }
  };
  
  // Helper function to determine transaction status
  const getTransactionStatus = (transactionType) => {
    switch (transactionType) {
      case 'PAYMENT':
      case 'RETURN':
        return 'PAID';
      case 'OPENING_BALANCE':
      case 'CREDIT_SALE':
        return 'PENDING';
      default:
        return 'PENDING';
    }
  };
  
  useEffect(() => {
    if (customerId) {
      fetchCustomerDetails();
    }
  }, [customerId]);
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date string properly to handle various date formats
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      console.log('Formatting date:', dateString, 'Type:', typeof dateString);
      
      // Handle different date formats
      let date;
      
      if (typeof dateString === 'string') {
        // For ISO format with time (YYYY-MM-DDTHH:MM:SS)
        if (dateString.includes('T')) {
          date = new Date(dateString);
        } 
        // For ISO date format (YYYY-MM-DD)
        else if (dateString.includes('-')) {
          date = new Date(dateString);
        }
        // For slash format (MM/DD/YYYY)
        else if (dateString.includes('/')) {
          date = new Date(dateString);
        }
        // For timestamp or numeric format
        else if (!isNaN(Number(dateString))) {
          date = new Date(Number(dateString));
        }
        // Other string formats
        else {
          date = new Date(dateString);
        }
      } else if (typeof dateString === 'number') {
        // Handle timestamp directly
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else if (dateString && dateString.year && dateString.monthValue && dateString.dayOfMonth) {
        // Handle Java LocalDate format
        date = new Date(dateString.year, dateString.monthValue - 1, dateString.dayOfMonth);
      } else {
        console.log('Unrecognized date format:', dateString);
        return 'N/A';
      }
      
      // Check if date is valid
      if (date instanceof Date && !isNaN(date)) {
        console.log('Converted date:', date);
        // Format as DD/MM/YYYY
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      
      console.log('Invalid date after conversion:', date);
      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'N/A';
    }
  };
  
  // Modify the print ledger function to use a more sophisticated approach
  const handleExportToExcel = async () => {
    try {
      // Prepare summary data (as array of arrays)
      const customerSummaryData = [
        ['CUSTOMER LEDGER REPORT'],
        ['Generated on:', new Date().toLocaleDateString()],
        [''],
        ['CUSTOMER INFORMATION'],
        ['Customer Name', customer?.name || 'N/A'],
        ['Phone', customer?.phone || 'N/A'],
        ['Email', customer?.email || 'N/A'],
        ['Address', customer?.address || 'N/A'],
        [''],
        ['ACCOUNT SUMMARY'],
        ['Opening Balance', '$' + accountSummary.openingBalance.toFixed(2)],
        ['Total Credit Sales', '$' + accountSummary.totalInvoice.toFixed(2)],
        ['Total Payments', '$' + accountSummary.totalPaid.toFixed(2)],
        ['Total Returns', '$' + accountSummary.totalReturns.toFixed(2)],
        ['Current Balance Due', '$' + accountSummary.balanceDue.toFixed(2)],
        [''], // Blank row before transactions
        ['Ledger Transactions:'],
      ];

      // Prepare transactions data (as array of objects)
      const transactionsData = transactions.map((transaction, index) => ({
        'S.No': index + 1,
        'Date': transaction.displayDate,
        'Description': transaction.description,
        'Type': transaction.type,
        'Reference': transaction.reference || 'N/A',
        'Debit': transaction.debit.toFixed(2),
        'Credit': transaction.credit.toFixed(2),
        'Balance': transaction.balance >= 0 
          ? `${transaction.balance.toFixed(2)} (dr)`
          : `${Math.abs(transaction.balance).toFixed(2)} (cr)`,
        'Status': transaction.status
      }));

      // Convert transactions to sheet (array of arrays, including headers)
      const transactionHeaders = [
        'S.No', 'Date', 'Description', 'Type', 'Reference', 'Debit', 'Credit', 'Balance', 'Status'
      ];
      const transactionRows = transactionsData.map(tx =>
        transactionHeaders.map(header => tx[header])
      );

      // Combine summary and transactions into one array of arrays
      const finalSheetData = [
        ...customerSummaryData,
        transactionHeaders,
        ...transactionRows
      ];

      // Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet(finalSheetData);
      ws['!cols'] = [
          { wch: 8 },   // S.No
          { wch: 12 },  // Date
          { wch: 30 },  // Description
          { wch: 15 },  // Type
          { wch: 20 },  // Reference
          { wch: 12 },  // Debit
          { wch: 12 },  // Credit
          { wch: 12 },  // Balance
          { wch: 12 }   // Status
        ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customer Ledger');

      // Generate and download Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileData = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer_ledger_${customer?.name || 'unknown'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Customer ledger exported to Excel successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Excel export failed:', error);
      enqueueSnackbar('Failed to export customer ledger to Excel', { variant: 'error' });
    }
  };

  // Temporary function to recalculate running balances
  const handleRecalculateBalances = async () => {
    try {
      console.log('Triggering recalculation for customer:', customerId);
      const result = await creditService.recalculateRunningBalances(customerId);
      console.log('Recalculation result:', result);
      alert('Running balances recalculated successfully!\n\nResult: ' + result + '\n\nPlease refresh the page to see updated data.');
    } catch (error) {
      console.error('Recalculation failed:', error);
      alert('Recalculation failed: ' + error);
    }
  };

  // Function to migrate existing credit sales
  const handleMigrateExistingCreditSales = async () => {
    try {
      console.log('Migrating existing credit sales for customer:', customerId);
      const result = await creditService.migrateExistingCreditSales(customerId);
      console.log('Migration result:', result);
      
      const message = `Migration completed for ${result.customerName}:\n\n` +
        `• Total credit sales found: ${result.totalCreditSalesFound}\n` +
        `• Migrated: ${result.migratedCount}\n` +
        `• Skipped: ${result.skippedCount}\n\n` +
        (result.migratedOrders.length > 0 ? `Migrated orders: ${result.migratedOrders.join(', ')}\n\n` : '') +
        'Please refresh the page to see updated transactions.';
        
      alert(message);
    } catch (error) {
      console.error('Migration failed:', error);
      alert('Migration failed: ' + error);
    }
  };

  // Debug function to check balance discrepancies
  const handleDebugBalances = async () => {
    try {
      console.log('Debugging balances for customer:', customerId);
      const summary = await creditService.getCustomerCreditSummary(customerId);
      console.log('Credit summary:', summary);
      
      const message = `Balance Debug for Customer ${customerId}:\n\n` +
        `Opening Balance: $${summary.openingBalance.toFixed(2)}\n` +
        `Total Credit Sales: $${summary.totalCreditSales.toFixed(2)}\n` +
        `Total Payments: $${summary.totalPayments.toFixed(2)}\n` +
        `Total Returns: $${summary.totalReturns.toFixed(2)}\n\n` +
        `Calculated Balance: $${summary.calculatedBalanceDue ? summary.calculatedBalanceDue.toFixed(2) : 'N/A'}\n` +
        `Authoritative Balance: $${summary.balanceDue.toFixed(2)}\n` +
        `Current Balance: $${summary.currentBalance ? summary.currentBalance.toFixed(2) : 'N/A'}\n\n` +
        `Discrepancy: $${summary.calculatedBalanceDue ? (summary.balanceDue - summary.calculatedBalanceDue).toFixed(2) : 'N/A'}`;
        
      alert(message);
    } catch (error) {
      console.error('Debug failed:', error);
      alert('Debug failed: ' + error);
    }
  };

  return (
    <Layout>
      {/* Add the print styles */}
      <style id="print-styles">{printStyles}</style>
      
      <Box sx={{ p: 3 }} className="customer-ledger-page">
        {/* Header with breadcrumbs and actions - will be hidden in print */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
          <Breadcrumbs aria-label="breadcrumb">
            <Link 
              to="/reports/credit-customers" 
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
            >
              <BackIcon fontSize="small" sx={{ mr: 0.5 }} />
              Credit Customers
            </Link>
            <Typography color="text.primary">Customer Ledger</Typography>
          </Breadcrumbs>
          
          <Button 
            variant="outlined" 
            startIcon={<ExcelIcon />} 
            onClick={handleExportToExcel}
          >
            Export to Excel
          </Button>
          
          {/* Temporary button for recalculation */}
          <Button 
            variant="contained" 
            color="secondary"
            sx={{ ml: 2 }}
            onClick={handleRecalculateBalances}
          >
            Recalculate Balances
          </Button>
          
          {/* Button for migrating existing credit sales */}
          <Button 
            variant="contained" 
            color="warning"
            sx={{ ml: 2 }}
            onClick={handleMigrateExistingCreditSales}
          >
            Migrate Credit Sales
          </Button>
          
          {/* Button for debugging balances */}
          <Button 
            variant="contained" 
            color="info"
            sx={{ ml: 2 }}
            onClick={handleDebugBalances}
          >
            Debug Balances
          </Button>
        </Box>
        
        {/* Main content - will be visible in print */}
        <Box className="print-container customer-ledger-content">
          {/* Print-only header */}
          <Box className="print-header print-only" sx={{ display: 'none' }}>
            <Typography variant="h4" gutterBottom>CUSTOMER LEDGER</Typography>
            <Typography variant="subtitle1">
              {new Date().toLocaleDateString()} | Tiya Inventory Management System
            </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : customer ? (
          <>
            {/* Customer Info Card */}
            <Paper sx={{ mb: 4, p: 3, borderRadius: 2 }}>
              <Grid container spacing={3}>
                {/* Customer Name and Basic Info */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h4" component="h1">
                      {customer.name}
                    </Typography>
                    <Chip 
                      label="Customer" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 2 }}
                    />
                  </Box>
                  
                  {/* Contact Information */}
                  <Box sx={{ ml: 5 }}>
                    {customer.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{customer.phone}</Typography>
                      </Box>
                    )}
                    
                    {customer.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{customer.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                {/* Address */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <AddressIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body1" component="div">
                        {customer.address && (
                          <Box component="span" sx={{ display: 'block' }}>
                            {customer.address}
                          </Box>
                        )}
                        {customer.city && (
                          <Box component="span" sx={{ display: 'block' }}>
                            {customer.city}
                          </Box>
                        )}
                        {customer.state && (
                          <Box component="span" sx={{ display: 'block' }}>
                            {customer.state}
                          </Box>
                        )}
                        {customer.postalCode && (
                          <Box component="span" sx={{ display: 'block' }}>
                            {customer.postalCode}
                          </Box>
                        )}
                        {customer.country && (
                          <Box component="span" sx={{ display: 'block' }}>
                            {customer.country}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Account Summary Card */}
            <Paper sx={{ mb: 4, overflow: 'hidden', borderRadius: 2 }}>
              <Box sx={{ bgcolor: '#1976d2', color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Account Summary
                </Typography>
                <Typography variant="body2">
                  {formatDate(dateRange.startDate)} To {formatDate(dateRange.endDate)}
                </Typography>
              </Box>
              
              <Grid container>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Opening Balance
                    </Typography>
                    <Typography variant="h6">
                      $ {accountSummary.openingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total invoice
                    </Typography>
                    <Typography variant="h6">
                      $ {accountSummary.totalInvoice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total paid
                    </Typography>
                    <Typography variant="h6">
                      $ {accountSummary.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total returns
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      $ {accountSummary.totalReturns.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#fafafa', height: '100%' }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Balance due
                    </Typography>
                    <Typography variant="h6" color="error">
                      $ {accountSummary.balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Ledger Table */}
            <Paper sx={{ mb: 4, borderRadius: 2 }}>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                <Typography variant="h6">
                  Ledger Transactions
                </Typography>
              </Box>
              
              {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Debit ($)</TableCell>
                          <TableCell align="right">Credit ($)</TableCell>
                          <TableCell align="right">Balance ($)</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.length > 0 ? (
                          transactions
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((transaction) => (
                              <TableRow key={transaction.id}>
                                <TableCell>
                                  {(() => {
                                    // Just display the pre-formatted date
                                    const displayDate = transaction.displayDate;
                                    console.log('Using pre-formatted date for display:', displayDate);
                                    return displayDate;
                                  })()}
                                </TableCell>
                                <TableCell>{transaction.type}</TableCell>
                                <TableCell>{transaction.reference}</TableCell>
                                <TableCell>{transaction.description}</TableCell>
                                <TableCell align="right">
                                  {transaction.debit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right">
                                  {transaction.credit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {transaction.balance >= 0
                                    ? `${transaction.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (dr)`
                                    : `${Math.abs(transaction.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (cr)`
                                  }
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={transaction.status} 
                                    size="small"
                                    color={transaction.status === 'PAID' ? 'success' : 'warning'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No transactions found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={transactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              )}
            </Paper>
          </>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Customer not found
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              sx={{ mt: 2 }}
              onClick={() => navigate('/reports/credit-customers')}
            >
              Back to Customers
            </Button>
          </Paper>
        )}
        </Box>
      </Box>
    </Layout>
  );
};

export default CustomerLedger; 